import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import Pagination from '../../../components/common/Pagination';
import ModuleHeaderStrip from '../../../components/common/ModuleHeaderStrip';

const DEFAULT_CLASSES = [
  'Pre-Primary (Nursery)', 'Pre-Primary (LKG)', 'Pre-Primary (UKG)',
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
  'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
  'Class 11 (Science)', 'Class 11 (Commerce)', 'Class 11 (Arts)',
  'Class 12 (Science)', 'Class 12 (Commerce)', 'Class 12 (Arts)',
];
const DEFAULT_SUBJECTS = [
  'Mathematics', 'Science', 'English', 'Social Studies', 'Hindi', 'Tamil',
  'Computer Science', 'Physics', 'Chemistry', 'Biology', 'History', 'Geography',
  'Economics', 'Commerce', 'Accountancy', 'Business Studies', 'Physical Education',
  'Arts & Craft', 'Music', 'Environmental Science',
];
const DEFAULT_EXAM_TYPES = [
  'Slip Test', 'Unit Test', 'Midterm', 'Quarterly',
  'Half-Yearly', 'Annual', 'Revision Test', 'Pre-Board',
];
const SECTIONS = ['A', 'B', 'C', 'D', 'E'];
const STORAGE_KEY = 'custom_exam_types';

function getExamTypes() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return [...DEFAULT_EXAM_TYPES, ...stored.filter(t => !DEFAULT_EXAM_TYPES.includes(t))];
  } catch { return DEFAULT_EXAM_TYPES; }
}

const BLANK = {
  exam_type: '', class_grade: '', section: '', subject: '',
  exam_date: '', total_marks: 100, passing_marks: 35,
  duration_minutes: 60, academic_year: '',
};

export default function ExamsPage() {
  const [exams,       setExams]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [form,        setForm]        = useState(BLANK);
  const [saving,      setSaving]      = useState(false);
  const [toast,       setToast]       = useState({ show: false, message: '', type: 'success' });
  const [examTypes,   setExamTypes]   = useState(getExamTypes());
  const [classOptions,setClassOptions]= useState(DEFAULT_CLASSES);
  const [sectionOptions, setSectionOptions] = useState(['A', 'B', 'C', 'D', 'E']);
  const [subjectOpts, setSubjectOpts] = useState(DEFAULT_SUBJECTS);
  const [showAddType, setShowAddType] = useState(false);
  const [newType,     setNewType]     = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterType,  setFilterType]  = useState('');
  const [editing,     setEditing]     = useState(null);

  const academicYears = (() => {
    const y = new Date().getFullYear();
    return [
      `${y - 1}-${String(y).slice(-2)}`,
      `${y}-${String(y + 1).slice(-2)}`,
      `${y + 1}-${String(y + 2).slice(-2)}`,
    ];
  })();

  // Load school config for class/subject dropdowns
  useEffect(() => {
    api.get('/records/school_config', { params: { limit: 1 } })
      .then(r => {
        const rec = r.data?.records?.[0]?.recordData || r.data?.record?.recordData;
        if (rec) {
          // Use explicit classes array if defined, otherwise use total_classes to generate
          if (rec.classes?.length) {
            setClassOptions(rec.classes);
          } else if (rec.total_classes) {
            const generated = [];
            for (let i = 1; i <= Number(rec.total_classes); i++) generated.push(`Class ${i}`);
            if (generated.length > 0) setClassOptions(generated);
          }
          if (rec.sections?.length) setSectionOptions(rec.sections);
          // Use explicit subjects array if defined
          if (rec.subjects?.length) setSubjectOpts(rec.subjects);
        }
      }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (filterClass) params['recordData.class_grade'] = filterClass;
    if (filterType)  params['recordData.exam_type']   = filterType;
    const r = await api.get('/education/exams', { params })
      .catch(() => ({ data: { records: [], total: 0 } }));
    setExams(r.data.records || []);
    setTotal(r.data.total || 0);
    setLoading(false);
  }, [page, filterClass, filterType]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing?._id) await api.put(`/education/exams/${editing._id}`, form);
      else              await api.post('/education/exams', form);
      setToast({ show: true, message: `Exam ${editing ? 'updated' : 'scheduled'}!`, type: 'success' });
      setShowForm(false); setForm(BLANK); setEditing(null); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error', type: 'danger' });
    } finally { setSaving(false); }
  };

  const openEdit = (exam) => {
    setForm(exam.recordData || {});
    setEditing(exam);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this exam?')) return;
    await api.delete(`/education/exams/${id}`).catch(() => {});
    load();
  };

  const addCustomType = () => {
    if (!newType.trim()) return;
    const updated = [...new Set([...examTypes, newType.trim()])];
    setExamTypes(updated);
    const custom = updated.filter(t => !DEFAULT_EXAM_TYPES.includes(t));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(custom));
    setForm(f => ({ ...f, exam_type: newType.trim() }));
    setNewType(''); setShowAddType(false);
  };

  const removeCustomType = (type) => {
    if (DEFAULT_EXAM_TYPES.includes(type)) return;
    const updated = examTypes.filter(t => t !== type);
    setExamTypes(updated);
    const custom = updated.filter(t => !DEFAULT_EXAM_TYPES.includes(t));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(custom));
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div>
      <PageHeader
        title="Examination Management"
        icon="bi-journal-check"
        subtitle="Schedule and manage all examinations"
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <select className="form-select form-select-sm" style={{ width: 160 }}
              value={filterClass} onChange={e => { setFilterClass(e.target.value); setPage(1); }}>
              <option value="">All Classes</option>
              {classOptions.map(c => <option key={c}>{c}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 140 }}
              value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {examTypes.map(t => <option key={t}>{t}</option>)}
            </select>
            <button className="btn btn-primary btn-sm"
              onClick={() => { setShowForm(s => !s); setEditing(null); setForm(BLANK); }}>
              <i className="bi bi-plus-lg me-1"></i>Schedule Exam
            </button>
          </div>
        }
      />

      <ModuleHeaderStrip
        icon="bi-journal-check"
        label="Examinations"
        chips={[
          { label: 'Active classes', value: classOptions.length },
          { label: 'Exam types', value: examTypes.length },
        ]}
        variant="warning"
      />

      {/* Form */}
      {showForm && (
        <div className="card mb-4" style={{ border: '2px solid #c7d2fe', borderRadius: 12 }}>
          <div className="card-header d-flex justify-content-between align-items-center"
            style={{ background: '#f5f3ff' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-calendar-plus me-2 text-primary"></i>
              {editing ? 'Edit Examination' : 'Schedule New Examination'}
            </span>
            <button className="btn-close" onClick={() => { setShowForm(false); setEditing(null); }}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">

                {/* Exam Type */}
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Exam Type *</label>
                  <div className="d-flex gap-1">
                    <select className="form-select" required value={form.exam_type}
                      onChange={e => f('exam_type', e.target.value)}>
                      <option value="">Select type</option>
                      {examTypes.map(t => <option key={t}>{t}</option>)}
                    </select>
                    <button type="button" className="btn btn-outline-secondary"
                      title="Add custom exam type"
                      onClick={() => setShowAddType(s => !s)}
                      style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                      <i className="bi bi-plus"></i>
                    </button>
                  </div>
                  {showAddType && (
                    <div className="d-flex gap-1 mt-1">
                      <input className="form-control form-control-sm"
                        placeholder="New exam type…"
                        value={newType}
                        onChange={e => setNewType(e.target.value)}
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomType(); } }} />
                      <button type="button" className="btn btn-success btn-sm" onClick={addCustomType}>Add</button>
                      <button type="button" className="btn btn-outline-secondary btn-sm"
                        onClick={() => setShowAddType(false)}>✕</button>
                    </div>
                  )}
                  {/* Show custom types with delete option */}
                  {examTypes.filter(t => !DEFAULT_EXAM_TYPES.includes(t)).length > 0 && (
                    <div className="mt-1 d-flex flex-wrap gap-1">
                      {examTypes.filter(t => !DEFAULT_EXAM_TYPES.includes(t)).map(t => (
                        <span key={t} className="badge d-flex align-items-center gap-1"
                          style={{ background: '#ede9fe', color: '#6366f1', fontSize: 10 }}>
                          {t}
                          <button type="button" className="btn-close btn-close-sm"
                            style={{ fontSize: 8 }}
                            onClick={() => removeCustomType(t)}></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Class */}
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Class / Grade *</label>
                  <select className="form-select" required value={form.class_grade}
                    onChange={e => f('class_grade', e.target.value)}>
                    <option value="">Select class</option>
                    {classOptions.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>

                {/* Section */}
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Section</label>
                  <select className="form-select" value={form.section || ''}
                    onChange={e => f('section', e.target.value)}>
                    <option value="">All Sections</option>
                    {sectionOptions.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>

                {/* Subject */}
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Subject *</label>
                  <select className="form-select" required value={form.subject || ''}
                    onChange={e => f('subject', e.target.value)}>
                    <option value="">Select subject</option>
                    {subjectOpts.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>

                {/* Academic Year */}
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Academic Year *</label>
                  <select className="form-select" required value={form.academic_year || ''}
                    onChange={e => f('academic_year', e.target.value)}>
                    <option value="">Select year</option>
                    {academicYears.map(y => <option key={y}>{y}</option>)}
                  </select>
                </div>

                {/* Exam Date */}
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Exam Date *</label>
                  <input type="date" className="form-control" required
                    value={form.exam_date || ''}
                    onChange={e => f('exam_date', e.target.value)} />
                </div>

                {/* Total Marks */}
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Total Marks *</label>
                  <input type="number" className="form-control" required min={1}
                    value={form.total_marks || ''}
                    onChange={e => f('total_marks', e.target.value)} />
                </div>

                {/* Passing Marks */}
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Passing Marks *</label>
                  <input type="number" className="form-control" required min={0}
                    value={form.passing_marks || ''}
                    onChange={e => f('passing_marks', e.target.value)} />
                </div>

                {/* Duration */}
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Duration (mins)</label>
                  <input type="number" className="form-control" min={15}
                    value={form.duration_minutes || ''}
                    onChange={e => f('duration_minutes', e.target.value)} />
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-3 pt-3 border-top">
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  {editing ? 'Update Exam' : 'Schedule Exam'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0">
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th>Exam Type</th>
                    <th>Class</th>
                    <th>Sec.</th>
                    <th>Subject</th>
                    <th>Date</th>
                    <th>Total</th>
                    <th>Pass</th>
                    <th>Duration</th>
                    <th>Year</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {exams.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-5">
                        <i className="bi bi-journal-check display-4 d-block mb-3 opacity-25"></i>
                        No exams scheduled yet. Click "Schedule Exam" to add one.
                      </td>
                    </tr>
                  ) : exams.map(e => {
                    const d = e.recordData || {};
                    const isUpcoming = d.exam_date && new Date(d.exam_date) >= new Date();
                    return (
                      <tr key={e._id}>
                        <td>
                          <span className="badge"
                            style={{ background: '#ede9fe', color: '#6366f1', fontSize: 11 }}>
                            {d.exam_type || '—'}
                          </span>
                        </td>
                        <td className="fw-medium" style={{ fontSize: 12 }}>{d.class_grade || '—'}</td>
                        <td style={{ fontSize: 12 }}>{d.section || <span className="text-muted">All</span>}</td>
                        <td style={{ fontSize: 12 }}>{d.subject || '—'}</td>
                        <td>
                          <div style={{ fontSize: 12 }}>
                            {d.exam_date ? new Date(d.exam_date).toLocaleDateString() : '—'}
                          </div>
                          {isUpcoming && (
                            <span className="badge"
                              style={{ background: '#e0f2fe', color: '#0369a1', fontSize: 9 }}>Upcoming</span>
                          )}
                        </td>
                        <td style={{ fontSize: 13, fontWeight: 600 }}>{d.total_marks || '—'}</td>
                        <td style={{ fontSize: 12, color: '#198754', fontWeight: 600 }}>{d.passing_marks || '—'}</td>
                        <td style={{ fontSize: 12 }}>{d.duration_minutes ? `${d.duration_minutes} min` : '—'}</td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>{d.academic_year || '—'}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <button className="btn btn-outline-primary btn-sm"
                              style={{ padding: '2px 6px' }} onClick={() => openEdit(e)}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="btn btn-outline-danger btn-sm"
                              style={{ padding: '2px 6px' }} onClick={() => handleDelete(e._id)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <Pagination page={page} totalPages={Math.ceil(total / 20)} onPageChange={setPage} total={total} limit={20} />
      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
